import { isSaid } from '../failure/said';

/**
 * The offline outbox rules, section 9.
 *
 * The lab has metal benches, a furnace and imperfect Wi-Fi. A technician whose
 * "mark done" fails silently stops using the system within a week and the whole
 * dataset becomes worthless, so this is a core feature rather than a nicety.
 *
 * These are the decisions the queue makes, kept pure and away from IndexedDB so
 * they can be reasoned about and tested directly. The storage and the sending
 * live in `infrastructure/offline`.
 */

export const OUTBOX_KINDS = [
  'start',
  'complete',
  'flag',
  'qcRework',
  'note',
  'photo',
  // A photograph of the finished work, queued in front of the completion it proves.
  'workProof',
] as const;

export type OutboxKind = (typeof OUTBOX_KINDS)[number];

export type OutboxStatus = 'pending' | 'sent' | 'failed';

export interface OutboxEntry {
  /** UUID v4, and also the Idempotency-Key. Section 7.4. */
  id: string;
  kind: OutboxKind;
  kitId: string;
  payload: unknown;
  /** The client clock, corrected by the session drift offset. */
  createdAt: string;
  attempts: number;
  lastError: string | null;
  status: OutboxStatus;
}

/** A sent entry is kept this long so a replay can still recognise it. */
export const PRUNE_AFTER_MS = 24 * 60 * 60 * 1000;

/**
 * Whether a failed send should be retried or given up on.
 *
 * A missing status means the request never reached the server: no network, DNS
 * gone, the furnace corner of the building. Those always retry.
 *
 * 408 and 429 are the server asking for the request again, so they retry. Every
 * other 4xx is the server saying the request itself is wrong, and retrying it
 * forever would spin until the battery died. Those become `failed` and are
 * shown in the tray rather than discarded, because a technician's completed
 * work is not ours to throw away quietly. Section 9 rule 4.
 *
 * 5xx retries: the request was valid and the server is having a bad minute.
 */
export function isRetryable(status: number | null): boolean {
  if (status === null) return true;
  if (status === 408 || status === 429) return true;
  return status < 400 || status >= 500;
}

/**
 * The HTTP status behind a rejected send, or null when there was not one.
 *
 * Structural rather than an `instanceof ApiError` check, because domain cannot
 * import infrastructure and should not know the class exists. Null covers both
 * "the network never answered" and "something else threw", and `isRetryable`
 * treats both the same way: try again later.
 */
export function httpStatusOf(error: unknown): number | null {
  if (typeof error !== 'object' || error === null || !('status' in error)) return null;
  const { status } = error;
  return typeof status === 'number' ? status : null;
}

/** What is written beside an entry when no answer came at all, for the tray to put into words. */
export const OUTBOX_NO_ANSWER = 'no-answer';

/**
 * What is written beside an entry that was not sent, for the tray to say why.
 *
 * It was the error's own message, whatever had raised it: "Failed to fetch" on
 * one phone, "Load failed" on another, and through the service worker a line
 * of its code. A sentence written for a person is kept as it is, the server's
 * refusal above all. Anything else is kept as what happened and not as what a
 * browser called it: the status when there was an answer, and the mark for no
 * answer when there was none. The lab's request of 9 October 2026.
 */
export function outboxReason(error: unknown): string {
  if (isSaid(error)) return error.message;
  const status = httpStatusOf(error);
  return status === null ? OUTBOX_NO_ANSWER : String(status);
}

/**
 * How long to wait before the next attempt, in milliseconds.
 *
 * Exponential with a ceiling, because a hundred phones reconnecting when the
 * access point comes back must not arrive as one wave. The ceiling is a minute:
 * beyond that a technician watching the tray would think it had stopped.
 */
export function backoffMs(attempts: number): number {
  return Math.min(60_000, 1000 * 2 ** Math.max(0, attempts - 1));
}

/**
 * Section 9 rule 5. Replay preserves order per kit; different kits may sync in
 * parallel.
 *
 * Order within a kit is not cosmetic: start then complete advances a stage,
 * while complete then start is a 409 and a stuck queue. Ordering is by
 * `createdAt` and then by id, so two commands stamped in the same millisecond
 * still replay the same way on every device.
 */
export function groupByKit(entries: readonly OutboxEntry[]): OutboxEntry[][] {
  const groups = new Map<string, OutboxEntry[]>();
  for (const entry of entries) {
    const group = groups.get(entry.kitId);
    if (group === undefined) groups.set(entry.kitId, [entry]);
    else group.push(entry);
  }

  return [...groups.values()].map((group) =>
    [...group].sort(
      (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
    ),
  );
}

/** Entries waiting to be sent, oldest first. */
export function pendingOf(entries: readonly OutboxEntry[]): OutboxEntry[] {
  return entries.filter((entry) => entry.status === 'pending');
}

/** Entries the server refused, which the tray has to show. */
export function failedOf(entries: readonly OutboxEntry[]): OutboxEntry[] {
  return entries.filter((entry) => entry.status === 'failed');
}

/**
 * Section 9 rule 2. A sent entry is pruned once it is a day old.
 *
 * Pending and failed entries are never pruned by age. A phone left in a drawer
 * over a weekend must still hold the work it recorded on Friday.
 */
export function isPrunable(entry: OutboxEntry, now: number): boolean {
  if (entry.status !== 'sent') return false;
  return now - Date.parse(entry.createdAt) >= PRUNE_AFTER_MS;
}

/**
 * What the tray shows for one refused entry. The kind is named in the
 * technician's words rather than as the command name, because "qcRework" means
 * nothing to the person looking at the tray.
 */
export const OUTBOX_KIND_LABEL: Record<OutboxKind, string> = {
  start: 'Start work',
  complete: 'Mark done',
  flag: 'Flag',
  qcRework: 'Send to rework',
  note: 'Note',
  photo: 'Photo',
  workProof: 'Proof of work',
};
